import { useState } from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { CITIES, ROUTES } from "../game/data";
import type { GameView, Route } from "../game/types";

export const INK: Record<string, string> = {
  purple: "#9972a9",
  white: "#f5efdc",
  blue: "#5e8fa9",
  yellow: "#dab64d",
  orange: "#d78c51",
  black: "#495157",
  red: "#bd5d4d",
  green: "#6c8d69",
  gray: "#b5ad98",
  locomotive: "#c49850",
};
export const cityName = (id: string) =>
  CITIES.find((c) => c.id === id)?.name ?? id;

function geometry(route: Route) {
  const a = CITIES.find((c) => c.id === route.from)!;
  const b = CITIES.find((c) => c.id === route.to)!;
  const twins = ROUTES.filter(
    (r) =>
      (r.from === route.from && r.to === route.to) ||
      (r.to === route.from && r.from === route.to),
  );
  const dx = b.x - a.x,
    dy = b.y - a.y,
    distance = Math.hypot(dx, dy);
  const index = twins.findIndex((r) => r.id === route.id);
  const offset = twins.length > 1 ? (index - (twins.length - 1) / 2) * 15 : 0;
  const canonical = route.from < route.to ? 1 : -1;
  const nx = (-dy / distance) * offset * canonical,
    ny = (dx / distance) * offset * canonical;
  return {
    a,
    b,
    dx,
    dy,
    distance,
    nx,
    ny,
    angle: (Math.atan2(dy, dx) * 180) / Math.PI,
  };
}

export function Board({
  game,
  selected,
  focusedCities = [],
  onRoute,
  onCity,
  decorative = false,
}: {
  game?: GameView | null;
  selected?: string | null;
  focusedCities?: string[];
  onRoute?: (route: Route) => void;
  onCity?: (id: string) => void;
  decorative?: boolean;
}) {
  const [zoom, setZoom] = useState(1);
  return (
    <div className={`board-shell ${decorative ? "decorative-board" : ""}`}>
      {!decorative && (
        <div className="map-toolbar">
          <span>
            EUROPE <span className="muted">/ 1901</span>
          </span>
          <div>
            <button
              aria-label="Zoom out"
              onClick={() => setZoom((z) => Math.max(1, z - 0.25))}
              disabled={zoom === 1}
            >
              <Minus size={15} />
            </button>
            <button aria-label="Reset zoom" onClick={() => setZoom(1)}>
              <RotateCcw size={14} />
            </button>
            <button
              aria-label="Zoom in"
              onClick={() => setZoom((z) => Math.min(2.5, z + 0.25))}
              disabled={zoom === 2.5}
            >
              <Plus size={15} />
            </button>
          </div>
        </div>
      )}
      <div className="map-scroll">
        <svg
          viewBox="0 0 1100 760"
          className="europe-map"
          style={{
            width: `${zoom * 100}%`,
            minWidth: decorative ? undefined : 700 * zoom,
          }}
          aria-label="Railway map of Europe"
          role={decorative ? "img" : "group"}
        >
          <defs>
            <pattern
              id="sea-lines"
              width="10"
              height="10"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(-30)"
            >
              <line y2="10" stroke="#50776c" strokeWidth=".35" opacity=".18" />
            </pattern>
            <pattern
              id="land-dots"
              width="8"
              height="8"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="1" cy="1" r=".5" fill="#5d735c" opacity=".14" />
            </pattern>
            <filter id="land-shadow">
              <feDropShadow
                dx="0"
                dy="3"
                stdDeviation="5"
                floodColor="#244b40"
                floodOpacity=".08"
              />
            </filter>
          </defs>
          <rect width="1100" height="760" fill="#c9dad2" />
          <rect width="1100" height="760" fill="url(#sea-lines)" />
          <g
            fill="#eee9d5"
            stroke="#a7baa2"
            strokeWidth="1.4"
            filter="url(#land-shadow)"
          >
            <path d="M41 573 35 641 48 684 77 704 120 722 158 716 179 687 233 674 272 643 280 617 302 598 337 557 377 559 416 542 450 518 470 539 481 565 512 591 541 610 554 642 570 655 588 642 576 618 578 596 608 616 622 607 600 580 568 559 553 528 533 505 538 485 559 490 584 527 627 562 655 608 670 625 691 643 700 674 718 693 748 701 757 685 744 667 770 646 789 629 813 633 842 643 869 629 877 601 847 578 826 550 839 518 878 489 906 482 929 499 948 527 972 512 978 480 1002 465 1029 474 1037 505 1060 544 1071 576 1100 593 1100 0 913 0 918 36 896 52 875 58 849 85 811 98 788 120 770 128 747 117 725 135 709 137 690 159 648 157 638 175 607 187 586 176 564 183 548 203 524 207 515 183 491 164 478 180 481 211 457 216 447 199 429 212 408 216 393 229 379 214 359 211 340 237 338 259 313 267 285 293 263 303 244 313 224 335 201 344 183 333 158 344 134 352 140 367 167 378 208 384 227 406 234 442 219 481 206 511 190 538 157 549 119 546 82 551Z" />
            <path d="M177 44 168 68 184 95 176 111 191 132 212 147 207 170 224 190 230 216 244 232 225 251 194 262 202 275 237 268 264 272 286 261 299 246 275 226 278 207 258 190 248 163 226 137 231 114 221 95 230 65 213 69 206 43 192 34Z" />
            <path d="M143 137 129 152 129 175 111 193 110 218 129 229 151 215 161 189 170 167 162 145Z" />
            <path d="M509 4 493 29 461 45 433 74 437 101 466 112 487 97 507 72 523 74 523 98 540 129 559 129 585 102 605 83 628 88 640 62 642 32 673 0Z" />
            <path d="M504 122 499 144 510 155 526 146 525 126Z M553 690 534 686 515 689 506 706 530 724 560 721 578 705Z" />
            <path d="M799 667 789 690 803 723 831 733 894 738 935 724 990 715 1050 697 1100 697 1100 618 1065 617 1024 626 995 640 959 633 925 644 895 654 870 649 837 661 820 658Z" />
            <path d="M441 558 433 574 437 597 446 592 449 573Z M440 618 432 633 438 655 449 650 449 628Z" />
            <path d="M719 718 747 718 768 725 759 733 726 728Z" />
          </g>
          <g
            fill="none"
            stroke="#b8bba3"
            strokeDasharray="3 5"
            strokeWidth="1"
            opacity=".65"
          >
            <path d="M195 537 Q266 571 318 580M330 285Q353 336 377 365M377 365Q421 390 397 441M391 474Q429 451 476 448M476 448Q544 401 590 440M594 310Q619 355 627 391M732 208Q779 290 768 375M790 491Q726 466 714 444" />
          </g>
          <g className="geographic-labels" fill="#618374" textAnchor="middle">
            <text x="108" y="460" transform="rotate(-65 108 460)">
              ATLANTIC OCEAN
            </text>
            <text x="354" y="120">
              NORTH SEA
            </text>
            <text x="651" y="128" transform="rotate(-20 651 128)">
              BALTIC SEA
            </text>
            <text x="360" y="686">
              MEDITERRANEAN SEA
            </text>
            <text x="935" y="583">
              BLACK SEA
            </text>
          </g>
          <g opacity=".2" fill="none" stroke="#56735a" strokeWidth="1.2">
            {Array.from({ length: 10 }, (_, i) => (
              <path
                key={i}
                d={`M${385 + i * 12} ${472 - Math.sin(i) * 7}l8 -12 9 12m-9 -12 2 8 3 -3`}
              />
            ))}
          </g>
          <g>
            {ROUTES.map((route) => {
              const g = geometry(route);
              const ownerId = game?.claimedRoutes[route.id];
              const owner = game?.players.find((p) => p.id === ownerId);
              const active = selected === route.id;
              const length = Math.max(
                5,
                (g.distance - 21 - (route.length - 1) * 4) / route.length,
              );
              const startX = g.a.x + (g.dx / g.distance) * 13 + g.nx,
                startY = g.a.y + (g.dy / g.distance) * 13 + g.ny;
              const label = `${cityName(route.from)} to ${cityName(route.to)}, ${route.length} ${route.color}${route.tunnel ? ", tunnel" : ""}${route.locomotives ? `, ${route.locomotives} locomotives required` : ""}${owner ? `, claimed by ${owner.name}` : ""}`;
              return (
                <g
                  key={route.id}
                  className={`map-route ${active ? "selected" : ""} ${owner ? "owned" : ""}`}
                  onClick={() => onRoute?.(route)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRoute?.(route);
                    }
                  }}
                  role={decorative ? undefined : "button"}
                  aria-label={label}
                  tabIndex={decorative ? undefined : 0}
                >
                  <title>{label}</title>
                  <path
                    d={`M${g.a.x + g.nx} ${g.a.y + g.ny}L${g.b.x + g.nx} ${g.b.y + g.ny}`}
                    stroke="transparent"
                    strokeWidth="18"
                    fill="none"
                  />
                  {Array.from({ length: route.length }, (_, i) => (
                    <g
                      key={i}
                      transform={`translate(${startX + (g.dx / g.distance) * i * (length + 4)} ${startY + (g.dy / g.distance) * i * (length + 4)}) rotate(${g.angle})`}
                      pointerEvents="none"
                    >
                      <rect
                        x="0"
                        y="-5"
                        width={length}
                        height="10"
                        rx="2"
                        fill={owner?.color ?? INK[route.color]}
                        stroke={
                          active ? "#173e34" : owner ? "#fff8e9" : "#655f50"
                        }
                        strokeWidth={active ? 2.8 : 1.1}
                      />
                      {route.tunnel && (
                        <path
                          d={`M1 -6V-8H${length - 1}V-6 M1 6V8H${length - 1}V6`}
                          fill="none"
                          stroke="#746650"
                          strokeWidth="1.1"
                        />
                      )}
                      {i < route.locomotives && (
                        <g
                          transform={`translate(${length / 2} 0)`}
                          fill={owner ? "#fff8e9" : "#4a493c"}
                        >
                          <rect x="-3" y="-3" width="6" height="5" rx="1" />
                          <path d="M-4 4-2 1H2L4 4Z" />
                        </g>
                      )}
                      {owner && (
                        <path
                          d={`M3 -2H${length - 3}M3 2H${length - 3}`}
                          stroke="#fff9e8"
                          strokeWidth="1"
                          opacity=".7"
                        />
                      )}
                    </g>
                  ))}
                </g>
              );
            })}
          </g>
          <g>
            {CITIES.map((city) => {
              const stationOwner = game?.players.find(
                (p) => p.id === game.stations[city.id],
              );
              return (
                <g
                  key={city.id}
                  className="map-city"
                  role={decorative ? undefined : "button"}
                  tabIndex={decorative ? undefined : 0}
                  aria-label={`${city.name}${stationOwner ? `, ${stationOwner.name}'s station` : ", build station"}`}
                  onClick={() => onCity?.(city.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onCity?.(city.id);
                    }
                  }}
                >
                  {focusedCities.includes(city.id) && (
                    <circle
                      cx={city.x}
                      cy={city.y}
                      r="16"
                      fill="#d39738"
                      fillOpacity=".2"
                      stroke="#b5762e"
                      strokeWidth="2"
                    />
                  )}
                  <circle cx={city.x} cy={city.y} r="12" fill="transparent" />
                  <circle
                    cx={city.x}
                    cy={city.y}
                    r="5.5"
                    fill={stationOwner?.color ?? "#f9f5e8"}
                    stroke="#344c3d"
                    strokeWidth="2"
                  />
                  {stationOwner && (
                    <path
                      d={`M${city.x - 7} ${city.y - 10}l7 -7 7 7v8h-14Z`}
                      fill={stationOwner.color}
                      stroke="#fff8e9"
                      strokeWidth="1.5"
                    />
                  )}
                  <text
                    x={city.x}
                    y={city.y - 12}
                    textAnchor="middle"
                    className="city-label"
                  >
                    {city.name}
                  </text>
                </g>
              );
            })}
          </g>
          <g
            transform="translate(1010 693)"
            stroke="#668276"
            fill="none"
            opacity=".65"
          >
            <circle r="22" strokeWidth=".6" />
            <path d="M0 -33V33M-33 0H33" strokeWidth=".7" />
            <path d="M0 -26 5 0 0 26-5 0Z" fill="#668276" />
            <text
              y="-39"
              stroke="none"
              fill="#668276"
              textAnchor="middle"
              fontSize="11"
            >
              N
            </text>
          </g>
          <text x="28" y="729" className="map-caption">
            A GRAND EUROPEAN RAILWAY ADVENTURE
          </text>
        </svg>
      </div>
      {!decorative && (
        <div className="map-legend">
          <span>
            <i className="legend-rail" /> Route
          </span>
          <span>
            <i className="legend-tunnel" /> Tunnel
          </span>
          <span>▣ Ferry locomotive</span>
          <span>⌂ Station</span>
          <span className="legend-tip">
            Select a route to claim it · Select a city to build
          </span>
        </div>
      )}
    </div>
  );
}
